import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import HomeCollegeCarouselCard from "./HomeCollegeCarouselCard";
import Cookies from "js-cookie";
import apiUrl from "../utility/apiUrl";
import { clearCachedList, fetchListDeduped, readCachedList } from "../utility/homeCollegeCache";
import { RevealGroup, revealItem } from "./ui/Reveal";
import { SkeletonCollegeCard } from "./ui/Skeleton";
import Button from "./ui/Button";

// Shared by the Home page's "Private Colleges" and "Featured" sections — previously
// PrivateCollege.js and FeaturedCollege.js, which were near-identical copies of each
// other differing only in cache key/endpoint.
//
// `endpoint` carries a fixed `?limit=48` (see HomePage.js) — that's the batch fetched
// once up front and cached (see homeCollegeCache.js), not a hard cap on how many
// colleges exist. There are hundreds of Private colleges and thousands of Featured
// candidates in the real data, so once a user reveals all 48 already-fetched cards,
// "Show More" now fetches the next backend page (?page=1, 2, ...) instead of quietly
// running out and hiding itself, which looked like "no more colleges" when far more
// were actually available (user-reported 2026-09-30).
const PAGE_SIZE_FALLBACK = 48;

const CollegeCarouselSection = ({ cacheKey, endpoint }) => {
  const [collegeData, setCollegeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [visibleCards, setVisibleCards] = useState(4); // Number of initially visible cards
  const [nextPage, setNextPage] = useState(1); // Page 0 is the initial fetch below
  const [hasMorePages, setHasMorePages] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  const [baseUrl, pageLimit] = useMemo(() => {
    const [path, query] = endpoint.split("?");
    const params = new URLSearchParams(query || "");
    const limit = parseInt(params.get("limit"), 10);
    return [path, Number.isFinite(limit) && limit > 0 ? limit : PAGE_SIZE_FALLBACK];
  }, [endpoint]);

  const buildHeaders = () => {
    const authToken = Cookies.get("token");
    const headers = {
      "Content-Type": "application/json",
      "ngrok-skip-browser-warning": "any",
    };
    if (authToken) headers.Authorization = String(authToken);
    return headers;
  };

  useEffect(() => {
    let cancelled = false;
    let retryTimer = null;

    const cached = readCachedList(cacheKey);
    if (cached) {
      setCollegeData(cached);
      setHasMorePages(cached.length >= pageLimit);
      setLoading(false);
    }
    setLoadFailed(false);

    const load = (retriesLeft) =>
      fetchListDeduped(cacheKey, apiUrl + endpoint, { headers: buildHeaders(), method: "POST" }).then((list) => {
        if (cancelled) return;
        const items = Array.isArray(list) ? list : [];
        // fetchListDeduped swallows network/server errors and hands back []. On a first visit
        // (cold backend, slow mobile network) that left a blank section until the visitor
        // refreshed, so quietly try once more before giving up and offering "Try again".
        if (items.length === 0 && retriesLeft > 0) {
          retryTimer = setTimeout(() => load(retriesLeft - 1), 1500);
          return;
        }
        setCollegeData(items);
        setHasMorePages(items.length >= pageLimit);
        setLoadFailed(items.length === 0);
        setLoading(false);
      });
    load(1);

    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, endpoint, reloadTick]);

  const retryLoad = () => {
    clearCachedList(cacheKey);
    setLoading(true);
    setReloadTick((t) => t + 1);
  };

  const showMore = async () => {
    if (loading) return;
    // Cards already fetched but not yet revealed — just reveal more, no network call.
    if (visibleCards < collegeData.length) {
      setVisibleCards((v) => v + 4);
      return;
    }
    if (!hasMorePages || loadingMore) return;

    setLoadingMore(true);
    try {
      const res = await fetch(`${apiUrl}${baseUrl}?limit=${pageLimit}&page=${nextPage}`, {
        method: "POST",
        headers: buildHeaders(),
      });
      const list = res.ok ? await res.json() : [];
      const fetched = Array.isArray(list) ? list : [];
      const existingIds = new Set(collegeData.map((c) => c?.id));
      const fresh = fetched.filter((c) => !existingIds.has(c?.id));

      setCollegeData((prev) => [...prev, ...fresh]);
      setVisibleCards((v) => v + 4);
      setNextPage((p) => p + 1);
      // No genuinely new colleges came back (e.g. tie-order overlap at the page
      // boundary) — treat that as having reached the real end, not an infinite loop.
      setHasMorePages(fresh.length > 0 && fetched.length >= pageLimit);
    } catch {
      setHasMorePages(false);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div>
      {loading && collegeData.length === 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <SkeletonCollegeCard key={i} />
          ))}
        </div>
      ) : loadFailed ? (
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-8 text-center">
          <p className="text-sm text-slate-600">Couldn't load colleges just now.</p>
          <Button type="button" onClick={retryLoad} variant="primary" className="mt-3 rounded-xl">
            Try again
          </Button>
        </div>
      ) : (
      <RevealGroup className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {collegeData.slice(0, visibleCards).map((details, index) => (
          <motion.div key={details?.id || index} variants={revealItem}>
            <HomeCollegeCarouselCard college={details} />
          </motion.div>
        ))}
      </RevealGroup>
      )}
      {/* Not while the first load is still in flight: `hasMorePages` starts true, so the button
          used to sit under the loading placeholders and a first-time tap fired a "next page"
          request before page 0 had even arrived - the two then raced, and it looked dead until
          a refresh served everything from cache. */}
      {!loading && !loadFailed && (visibleCards < collegeData.length || hasMorePages) && (
        <div className="mt-6 text-center">
          <Button type="button" onClick={showMore} variant="primary" className="rounded-xl" disabled={loadingMore}>
            {loadingMore ? "Loading…" : "Show More"}
          </Button>
        </div>
      )}
    </div>
  );
};

export default CollegeCarouselSection;
